import React, { useState } from 'react';
import { Clock, Users, Star, MapPin, Calendar } from 'lucide-react';
import type { Package } from '../types';
import NearbyAgenciesModal from './NearbyAgenciesModal';
import AirlineBadge from './AirlineBadge';
import { getImageUrl } from '../utils/imageUrl';
import { useNavigation } from '../contexts/NavigationContext';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

interface PackageProps {
  id: number;
  title: string;
  description: string;
  image: string;
  groupSize: string;
  category: string;
  type: 'B2B' | 'B2C';
  departure: string;
  departures: {
    id: number;
    departureDate: string;
    returnDate: string;
  }[];
  formulas: {
    id: number;
    name: string;
    priceType: string;
    perPersonPrice: number | null;
    roomPrices: {
      single: number;
      double: number;
      triple: number;
      quadruple: number;
      quintuple: number;
    };
  }[];
  agency?: {
    id: number;
    name: string;
    logo: string;
    rating?: number;
    verified: boolean;
  };
  airline?: {
    id: number;
    name: string;
    logo: string;
    code?: string | null;
  } | null;
}

interface PackageCardProps {
  package: PackageProps;
  primaryColor: string;
}

export default function PackageCard({ package: pkg, primaryColor }: PackageCardProps) {
  const { navigateToPackage } = useNavigation();
  const [showAgenciesModal, setShowAgenciesModal] = useState(false);
  const [showAllDates, setShowAllDates] = useState(false);

  const getDisplayPrice = (formula: PackageProps['formulas'][0]) => {
    if (formula.priceType === 'PER_PERSON' && formula.perPersonPrice) {
      return formula.perPersonPrice;
    }
    
    if (formula.roomPrices) {
      const prices = Object.values(formula.roomPrices).filter(price => price > 0);
      return prices.length > 0 ? Math.min(...prices) : 0;
    }

    return 0;
  };

  const lowestPrice = pkg.formulas.reduce((min, formula) => {
    const price = getDisplayPrice(formula);
    return Math.min(min, price);
  }, Infinity);

  // Obtenir la prochaine date de départ
  const getNextDeparture = () => {
    if (!pkg.departures || pkg.departures.length === 0) return null;

    const now = new Date();
    const futureDepartures = pkg.departures
      .filter(dep => new Date(dep.departureDate) >= now)
      .sort((a, b) => new Date(a.departureDate).getTime() - new Date(b.departureDate).getTime());

    return futureDepartures[0] || pkg.departures[0];
  };

  const nextDeparture = getNextDeparture();

  // Calcul de la durée basé sur la prochaine date de départ
  const getDuration = () => {
    if (!nextDeparture) return { days: 0, nights: 0 };

    try {
      const departureDate = new Date(nextDeparture.departureDate);
      const returnDate = new Date(nextDeparture.returnDate);
      
      if (isNaN(departureDate.getTime()) || isNaN(returnDate.getTime())) {
        return { days: 0, nights: 0 };
      }
      
      const days = differenceInDays(returnDate, departureDate);
      return {
        days: Math.max(days, 0),
        nights: Math.max(days - 1, 0)
      };
    } catch (error) {
      console.error('Erreur de calcul de durée:', error);
      return { days: 0, nights: 0 };
    }
  };

  const { days: daysCount, nights: nightsCount } = getDuration();

  // Trier les dates de départ
  const sortedDepartures = [...pkg.departures].sort(
    (a, b) => new Date(a.departureDate).getTime() - new Date(b.departureDate).getTime()
  );

  // Afficher soit toutes les dates, soit seulement les 3 premières
  const displayedDepartures = showAllDates ? sortedDepartures : sortedDepartures.slice(0, 3);

  // Ajout d'une fonction de validation de date
  const isValidDate = (date: any): boolean => {
    if (!date) return false;
    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime());
  };

  // Fonction de formatage de date sécurisée
  const formatDate = (date: any): string => {
    if (!isValidDate(date)) {
      return 'Date à confirmer';
    }
    try {
      return format(new Date(date), 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return 'Date à confirmer';
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
        {/* Image container avec badge de prix */}
        <div className="relative aspect-[3/2]">
          <img 
            src={getImageUrl(pkg.image)} 
            alt={pkg.title} 
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div 
            className="absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-semibold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            À partir de {lowestPrice.toLocaleString('fr-DZ')} DA
          </div>
          
          {/* Badge agence */}
          {pkg.agency && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="flex items-center gap-3">
                <img
                  src={getImageUrl(pkg.agency.logo)}
                  alt={pkg.agency.name}
                  className="w-10 h-10 rounded-full border-2 border-white/20 object-cover"
                />
                <div className="flex-1">
                  <h4 className="text-white font-medium">{pkg.agency.name}</h4>
                  <div className="flex items-center gap-2 text-white/90">
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="ml-1 text-sm">{pkg.agency.rating}</span>
                    </div>
                    {pkg.agency.verified && (
                      <span 
                        className="text-white text-xs px-2 py-0.5 rounded-full border"
                        style={{ 
                          backgroundColor: `${primaryColor}33`,
                          borderColor: `${primaryColor}4D`
                        }}
                      >
                        Vérifié
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Contenu */}
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-3 text-gray-900">{pkg.title}</h3>

          {pkg.airline ? (
            <div className="mb-3">
              <AirlineBadge airline={pkg.airline} variant="compact" />
            </div>
          ) : null}

          <div className="space-y-4 mb-4">
            {/* Dates de départ */}
            <div>
              <div className="flex items-center mb-2">
                <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Dates de départ</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {displayedDepartures.map((departure) => (
                  <span
                    key={departure.id}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${"#181105"}15`,
                      color: "#181105",
                      border: `1px solid ${primaryColor}30`
                    }}
                  >
                    {formatDate(departure.departureDate)}
                  </span>
                ))}
                {sortedDepartures.length > 3 && (
                  <button
                    onClick={() => setShowAllDates(!showAllDates)}
                    className="text-xs font-medium hover:underline"
                    style={{ color: primaryColor }}
                  >
                    {showAllDates ? 'Voir moins' : `+${sortedDepartures.length - 3} dates`}
                  </button>
                )}
              </div>
            </div>

            {/* Durée du séjour */}
            <div className="flex items-center text-sm text-gray-600">
              <Clock className="w-4 h-4 mr-2" />
              <span>{daysCount} jours / {nightsCount} nuits</span>
            </div>
            
            {/* Lieu de départ */}
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="w-4 h-4 mr-2" />
              <span>Départ de {pkg.departure}</span>
            </div>
          </div>
          
          <p className="text-gray-600 mb-6 line-clamp-2">{pkg.description}</p>
          
          <div className="flex gap-3">
            <button 
              onClick={() => navigateToPackage(pkg.id)}
              className="flex-1 text-white py-2 px-4 rounded-lg hover:opacity-90 transition text-center cursor-pointer"
              style={{ backgroundColor: primaryColor }}
            >
              Voir les détails
            </button>
            <button
              onClick={() => setShowAgenciesModal(true)}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition text-sm"
              style={{ 
                borderColor: primaryColor,
                color: primaryColor
              }}
            >
              <MapPin className="w-4 h-4" />
              Réserver
            </button>
          </div>
        </div>
      </div>

      <NearbyAgenciesModal
        isOpen={showAgenciesModal}
        onClose={() => setShowAgenciesModal(false)}
        agencies={pkg.agency ? [pkg.agency] : []}
      />
    </>
  );
}